import { EnterFlowFeature } from 'components/enter/constants/EnterFlowFeature';
import { EntryMethod } from 'components/enter/EntryMethod';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import Router from 'next/router';
import { IEntryLine } from 'types/Draws';
import {
  EecProduct,
  fetchUtmParamsJoinedWithFbDataFromLocalStorage,
  trackEecAddToCart,
  trackEecCheckoutStep1,
  trackEecCheckoutStep2,
  trackEecCheckoutStep4,
  trackEvent,
  trackInitiateCheckoutEvent,
  trackPurchase,
} from 'utils/analytics/analytics';
import { createSubscriptionDetailsObject } from 'utils/api/order';
import { getSpendLimitErrorMessage } from 'utils/common';
import { PaymentSchedule } from 'utils/constants';
import { getCookie, removeCookie } from 'utils/cookie';
import { navigateToConfirmation } from 'utils/navigation';
import { generateRandomNumbers } from 'utils/TicketUtil';
import {
  lineIsValid,
  ticketContainsDuplicateLines,
} from 'utils/TicketValidation';
import { RootStore } from './RootStore';

import {
  OrderStatusEnum,
  PromoRewardType,
  TPromoCodeSelect,
} from '@daymade/api-contracts';
import { promoCodeConstants } from '@daymade/common-lib/dbConstants';
import { RewardType } from '@daymade/common-lib/dbEnums';
import { TEntryLine } from '@daymade/common-lib/dbJsonModels';
import { exhaustiveCheck } from '@daymade/common-lib/types';
import {
  DaymadeDrawTypePlanCode,
  DaymadeDrawTypePlanSubcode,
  MainDrawDay,
} from 'constants/Draw';
import mixpanel from 'mixpanel-browser';
import { SOURCE_QUERY_PARAM } from 'pages/validate-3ds-payment';
import { TOrderDetails } from 'types/OrderDetails';
import { IPromoCodeApplicationResult } from 'types/PromoCode';
import {
  orderApiClient,
  promoApiClient,
  subscriptionApiClient,
} from 'utils/apiClient';
import { convertMicroCost } from 'utils/cost/convertMicroCost';
import { getDrawScheduleIds } from 'utils/draw/getDrawScheduleIds';
import HttpStatusCode from 'utils/HttpStatusCode';

type TAdyenPaymentSession = {
  id: string;
  sessionData: string;
  adyenPaymentSessionId: string;
};

const emptyLine = { main: [null, null, null, null], bonus: null };

const defaultDays = {
  [DaymadeDrawTypePlanCode.PREMIUM]: [MainDrawDay.TUESDAY],
  [DaymadeDrawTypePlanCode.LITE]: [MainDrawDay.TUESDAY],
  [DaymadeDrawTypePlanCode.FREE]: [MainDrawDay.FRIDAY],
};

export const ENTER_STORE_LOCAL_STORAGE_KEY = 'ENTER_STORE_DATA_V2';

export class EnterStore {
  root: RootStore;

  // Observable properties
  initialised: boolean = false;
  initialised_promocode: boolean = false;
  checked_user_referral: boolean = false;
  entryMethod: EntryMethod = EntryMethod.PremiumSubscription; // legacy
  planCode: DaymadeDrawTypePlanCode = DaymadeDrawTypePlanCode.PREMIUM;
  planSubcode: DaymadeDrawTypePlanSubcode = DaymadeDrawTypePlanSubcode.BASE;
  isUpgradeFlow: boolean = false;
  num_entries: number = 0;
  lines: IEntryLine[] = [];
  days: MainDrawDay[] = defaultDays[DaymadeDrawTypePlanCode.PREMIUM];
  paymentSchedule = PaymentSchedule.WEEKLY;
  boosted: boolean = false;
  referralId: string | null = null;
  promoCode: string | null = null;
  upgradePageSource = false;

  currentPromo: TPromoCodeSelect | null = null;
  promoErrorMessage: string | null = null;
  referralDetails: { firstName: string } | null = null;

  orderId: string | null = null;
  adyenPaymentSession: TAdyenPaymentSession | null = null;
  orderError: string | null = null;
  adyenPaymentSessionTime: number | null = null;
  loaded: boolean = false;
  isReferralFlow: boolean = false;
  loadingReferralBonusScheme: boolean = false;
  hiddenFeatures: string[] = [];
  /** Whether we have recently completed an order. This is used to suppress the creation of adyenPaymentSessions while
   * we are on the checkout page and we have literally just completed an order before redirecting to the confirmation
   * page.
   */
  orderCompleted: boolean = false;
  errorPopupMessage: string | null = null;

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      discountBannerText: true,
      setOrderCompleted: true,
      maybeContinueToPayment: true,
      root: true,
      planCode: true,
      planSubcode: true,
      upgradePageSource: true,
      getData: true,
      initReactions: true,
      currentUser: true,
      resetEnter: true,
      getRewardDiscount: true,
      setLineBonusNumber: true,
      handleContinueToPayment: true,
      checkOrderStatusAndAdvanceToConfirmation: true,
      onSuccessfulPayment: true,
      setIsReferralFlow: true,
      setLoadingReferralBonusScheme: true,
      setHiddenFeatures: true,
      hideFeatures: true,
      fetchReferralDetails: true,
      isUpgradeFlow: true,
      // Observables
      initialised: observable,
      initialised_promocode: observable,
      checked_user_referral: observable,
      entryMethod: observable,
      num_entries: observable,
      lines: observable,
      days: observable,
      paymentSchedule: observable,
      boosted: observable,
      referralId: observable,
      promoCode: observable,
      orderId: observable,
      adyenPaymentSession: observable,
      adyenPaymentSessionTime: observable,
      orderError: observable,
      loaded: observable,
      isReferralFlow: observable,
      loadingReferralBonusScheme: observable,
      hiddenFeatures: observable,
      orderCompleted: observable,

      currentPromo: observable,
      promoErrorMessage: observable,
      referralDetails: observable,
      errorPopupMessage: observable,

      setInitialised: action,
      setInitialised_promocode: action,
      setCheckedUserReferral: action,

      setupReferral: action,
      setupUpgradeConfig: action,

      setEntryMethod: action,
      setNumEntries: action,
      setLines: action,
      setLine: action,
      toggleLineNumber: action,
      toggleLineBonusNumber: action,
      generateLineRandomNumbers: action,
      generateRandomNumbers: action,
      removeLine: action,
      setLineNumber: action,
      setDays: action,
      toggleDay: action,
      setBoosted: action,
      setReferralId: action,
      setPromoCode: action,
      getDiscount: action,
      processPromoCode: action,
      setOrder: action,
      setOrderError: action,
      resetOrder: action,
      validateOrder: action,

      confirmed: computed,
      dayEnabled: computed,
      plan: computed,
      orderCost: computed,
      boostCostPerEntry: computed,
      haveValidAdyenPaymentSession: computed,
      promoCodeMessage: computed,
      promoCodeApplicationResult: computed,
      isValid: computed,

      eligibleForIntroOffer: computed,
      setLoaded: action,
      chooseNumbers: action,
      navigateToEnterTest: action,
      navigateToCheckout: action,
      setErrorPopupMessage: action,

      // Computed values
      isPaidSubscription: computed,
      isPremiumSubscription: computed,
      discountFirstEntry: computed,
      firstEntryDiscount: computed,
      isCurrentPromoFirstEntryDiscount: computed,
    });

    this.initReactions();
    this.fetchReferralDetails();
  }

  getData = () => {
    const persistAttributes: (keyof EnterStore)[] = [
      'entryMethod',
      'planCode',
      'planSubcode',
      'num_entries',
      'lines',
      'days',
      'paymentSchedule',
      'drawSchedules',
      'boosted',
      'promoCode',
      'upgradePageSource',
      'orderId',
      'adyenPaymentSession',
      'adyenPaymentSessionTime',
      'orderError',
      'isUpgradeFlow',
    ];
    return Object.fromEntries(persistAttributes.map((x) => [x, this[x]]));
  };

  initReactions() {
    if (typeof window === 'undefined') {
      return;
    }

    reaction(
      () => ({
        loadingReferralBonusScheme: this.loadingReferralBonusScheme,
        isReferralFlow: this.isReferralFlow,
        loaded: this.loaded,
        num_entries: this.num_entries,
        isPaidSubscription: this.isPaidSubscription,
        orderCompleted: this.orderCompleted,
        eligibleForIntroOffer: this.eligibleForIntroOffer,
        isUserStoreInitialized: this.root.userStore.initialized,
      }),
      async ({
        loadingReferralBonusScheme,
        isReferralFlow,
        loaded,
        num_entries,
        isPaidSubscription,
        orderCompleted,
        eligibleForIntroOffer,
        isUserStoreInitialized,
      }) => {
        if (
          !loadingReferralBonusScheme &&
          !loaded &&
          isPaidSubscription &&
          !orderCompleted
        ) {
          if (isReferralFlow) {
            trackEvent('isReferralFlow');
            this.setNumEntries(1);
            this.setDays(defaultDays[this.planCode]);
            this.setBoosted(false);
          } else {
            if ([0, 1].includes(num_entries)) {
              this.setNumEntries(1);
            }
            let ext_promo_code = null;
            if (typeof window !== 'undefined') {
              ext_promo_code =
                localStorage.getItem('promoCode') || getCookie('promo_code');
            }
            if (ext_promo_code) {
              await this.setPromoCode(ext_promo_code);
            } else if (
              isUserStoreInitialized &&
              eligibleForIntroOffer &&
              isPaidSubscription
            ) {
              await this.setFirstEntryOffer();
            } else {
              console.log('No valid promo code found');
              await this.setPromoCode(null);
            }
          }
          this.setInitialised_promocode(!loadingReferralBonusScheme);
        }
        this.setLoaded(true);
        this.setInitialised(true);
        this.validateOrder();
      },
      { fireImmediately: true }
    );

    // Reaction for loading referral data
    reaction(
      () => ({
        loadingReferralBonusScheme: this.loadingReferralBonusScheme,
        currentUser: this.currentUser,
        isReferralFlow: this.isReferralFlow,
      }),
      async ({ loadingReferralBonusScheme, currentUser, isReferralFlow }) => {
        if (!loadingReferralBonusScheme && currentUser) {
          if (isReferralFlow) {
            const referralId = Router.query.ri || getCookie('referral_id');
            this.setReferralId(referralId as string);
            await this.processPromoCode();
            this.setLoaded(true);
          }
          this.setCheckedUserReferral(true);
        }
      },
      { fireImmediately: true }
    );

    reaction(
      () => ({
        isUserStoreInitialized: this.root.userStore.initialized,
        eligibleForIntroOffer: this.eligibleForIntroOffer,
        isCurrentPromoFirstEntryDiscount: this.isCurrentPromoFirstEntryDiscount,
      }),
      async ({ eligibleForIntroOffer, isCurrentPromoFirstEntryDiscount }) => {
        if (!eligibleForIntroOffer && isCurrentPromoFirstEntryDiscount) {
          this.setPromoCode(null);
        }
      },
      { fireImmediately: true }
    );

    reaction(
      () => ({
        referralId: this.referralId,
      }),
      async ({ referralId }) => {
        if (referralId) {
          await this.processPromoCode();
        }
      }
    );
  }

  // Computed properties and getters
  get dayEnabled() {
    return Object.fromEntries(
      [MainDrawDay.TUESDAY, MainDrawDay.FRIDAY].map((day) => [
        day,
        this.days.includes(day),
      ])
    ) as Record<MainDrawDay, boolean>;
  }

  get drawSchedules() {
    return this.root.drawStore.drawSchedules;
  }

  get subscriptionStatus() {
    return this.root.userStore.subscriptionStatus;
  }

  get isPaidSubscription() {
    return [
      EntryMethod.PremiumSubscription,
      EntryMethod.LiteSubscription,
    ].includes(this.entryMethod);
  }

  get isPremiumSubscription() {
    return this.entryMethod === EntryMethod.PremiumSubscription;
  }

  get discountFirstEntry() {
    return (
      this.entryMethod === EntryMethod.PremiumSubscription &&
      !this.isReferralFlow &&
      this.eligibleForIntroOffer
    );
  }

  /** The **brief** summary text of the promo code, not to be confused with the promoCodeMessage */
  get discountBannerText() {
    if (this.isReferralFlow) {
      // ENG-1060 - Referrals temporarily disabled
      //return '4 weeks free'; - When we reenable referrals, maybe we should get this message off the promo code's banner?
      return undefined;
    }

    if (this.currentPromo) {
      // Show the banner in the promo, otherwise fallback to 'promo applied' since we don't know what to show
      return this.currentPromo?.banner || 'Promo applied';
    }

    return undefined;
  }

  get firstEntryDiscount() {
    return this.discountFirstEntry ? 0.5 : 0;
  }

  get currentUser() {
    return this.root.userStore.currentUser;
  }

  get isCurrentUserFtmp() {
    return this.root.userStore.isFtmp;
  }

  get eligibleForIntroOffer() {
    return this.root.userStore.eligibleForIntroOffer;
  }

  // Action methods
  setInitialised = (initialised: boolean) => {
    this.initialised = initialised;

    if (this.num_entries === 0) {
      this.resetEnter();
    }
  };

  setInitialised_promocode = (initialised_promocode: boolean) => {
    this.initialised_promocode = initialised_promocode;
  };

  setCheckedUserReferral = (checked_user_referral: boolean) => {
    this.checked_user_referral = checked_user_referral;
  };

  setLoaded = (loaded: boolean) => {
    this.loaded = loaded;
  };

  resetOrder = () => {
    this.orderId = null;
    this.adyenPaymentSession = null;
    this.adyenPaymentSessionTime = null;
    this.orderError = null;
    // NOTE - We do not clear this.orderCompleted here, as this resetOrder function is called multiple times by the
    // EnterStore's reaction
  };

  resetEnter = () => {
    this.setNumEntries(1);
    this.setBoosted(false);
    this.setDays(defaultDays[this.planCode]);
  };

  setupReferral = () => {
    trackEvent('isReferralFlow');
    this.setNumEntries(1);
    this.setDays(defaultDays[this.planCode]);
    this.setBoosted(false);
  };

  setIsUpgradeFlow = (isUpgrade: boolean) => {
    this.isUpgradeFlow = isUpgrade;
  };

  setEntryMethod = (entryMethod: EntryMethod) => {
    let newPlanCode = this.planCode;
    let newPlanSubcode = this.planSubcode;
    switch (entryMethod) {
      case EntryMethod.PremiumSubscription:
        newPlanCode = DaymadeDrawTypePlanCode.PREMIUM;

        break;
      case EntryMethod.LiteSubscription:
        newPlanCode = DaymadeDrawTypePlanCode.LITE;

        break;

      case EntryMethod.FreeSubscription:
        newPlanCode = DaymadeDrawTypePlanCode.FREE;
        newPlanSubcode = DaymadeDrawTypePlanSubcode.BASE;
        break;
    }

    if (
      this.planCode != newPlanCode ||
      this.planSubcode != newPlanSubcode ||
      this.entryMethod != entryMethod
    ) {
      this.entryMethod = entryMethod;
      this.planCode = newPlanCode;
      this.planSubcode = newPlanSubcode;

      this.resetEnter();
      console.log('Reset enter state');
      this.setPromoCode(null);
      this.resetOrder();
    }
  };

  goUpgradeOffer = async () => {
    if (!this.currentUser) {
      return;
    }

    const { body, status } = await promoApiClient.auth.getUpgradeOffer({
      query: {
        planCode: this.subscriptionStatus,
      },
    });

    if (status === HttpStatusCode.OK) {
      if (!body.promoCode) {
        mixpanel.track('Go Upgrade Offer No Promo Code', {
          message: 'No promo code available for upgrade offer',
        });
        return;
      }
      await Router.push(
        `/account/subscriptions/upgrade?code=${body.promoCode.code}`
      );
      return;
    } else if (status === HttpStatusCode.NOT_FOUND) {
      mixpanel.track('Go Upgrade Offer Not Found', {
        message: 'No first entry offer available',
      });
      return;
    } else {
      mixpanel.track('Go Upgrade Offer Failed', {
        status,
        message: body.message || 'No promo code available',
      });
    }
  };

  setupUpgradeConfig = async (entryMethod: EntryMethod, promoCode?: string) => {
    if (
      ![EntryMethod.PremiumSubscription, EntryMethod.LiteSubscription].includes(
        entryMethod
      )
    ) {
      throw new Error(`Invalid entry method: ${entryMethod}.`);
    }
    // Setup any configuration needed for the upgrade offer
    this.resetEnter();
    this.setEntryMethod(entryMethod);

    await this.setPromoCode(promoCode ?? null);

    this.setIsUpgradeFlow(true);

    let URL = '/enternow/1';
    if (entryMethod === EntryMethod.PremiumSubscription) {
      this.planCode = DaymadeDrawTypePlanCode.PREMIUM;
      URL = `/enternow/1${this.promoCode ? `?pc=${this.promoCode}` : ''}`;
    } else if (entryMethod === EntryMethod.LiteSubscription) {
      this.planCode = DaymadeDrawTypePlanCode.LITE;
      URL = `/enter?em=LiteSubscription${this.promoCode ? `&pc=${this.promoCode}` : ''}`;
    }

    this.resetOrder();
    Router.push(URL);
  };

  get plan() {
    return this.root.drawStore.drawTypePlans[this.planCode]
      ? this.root.drawStore.drawTypePlans[this.planCode][this.planSubcode]
      : null;
  }

  get orderCost() {
    let price = 0;
    let discount = 0;
    let cost = 0;

    if (!this.plan) {
      return {
        price,
        discount,
        cost,
      };
    }

    const numDraws = this.days.length;
    const numEntries = this.num_entries;

    const costPerEntry =
      this.plan.priceTable[this.plan.priceVersion]['GBP'][numEntries];

    price = costPerEntry * numDraws * numEntries;
    if (this.currentPromo) {
      discount = this.getRewardDiscount(
        this.currentPromo.rewardType,
        this.currentPromo.rewardValue,
        price,
        costPerEntry
      );
    }
    const unrounded_cost = price - discount;
    // tickets can't cost fractions of pennies. so round up
    const costPerTicket = Math.ceil(unrounded_cost / numDraws);

    // recalculate discount based on new rounded cost per ticket
    discount = price - costPerTicket * numDraws;

    cost = price - discount;

    return {
      price,
      discount,
      cost,
    };
  }

  getRewardDiscount = (
    rewardType: RewardType,
    rewardValue: number,
    price: number,
    pricePerEntry: number
  ) => {
    let discount = 0;

    if (rewardType === PromoRewardType.DISCOUNT_PERCENTAGE && rewardValue) {
      discount = (rewardValue / 100) * price;
    }

    if (rewardType === PromoRewardType.DISCOUNT_ENTRY && rewardValue) {
      discount = Math.min(rewardValue * pricePerEntry, price);
    }

    if (rewardType === PromoRewardType.FIXED_DISCOUNT && rewardValue) {
      discount = Math.min(rewardValue, price);
    }

    return discount;
  };

  get boostCostPerEntry() {
    let boostCostPerEntry = 0;
    if (
      this.root.drawStore.drawTypePlans[this.planCode] &&
      this.planCode !== DaymadeDrawTypePlanCode.FREE
    ) {
      const normalPlan =
        this.root.drawStore.drawTypePlans[this.planCode][
          DaymadeDrawTypePlanSubcode.BASE
        ];
      const normalPrice =
        normalPlan.priceTable[normalPlan.priceVersion]['GBP']['1'] || 0;

      const boostPlan =
        this.root.drawStore.drawTypePlans[this.planCode][
          DaymadeDrawTypePlanSubcode.BOOST
        ];
      const boostPrice =
        boostPlan.priceTable[boostPlan.priceVersion]['GBP']['1'] || 0;

      boostCostPerEntry = boostPrice - normalPrice;
    }

    return boostCostPerEntry;
  }

  setNumEntries = (num_entries: number) => {
    if (num_entries < 1) {
      num_entries = 1;
    }

    this.num_entries = num_entries;

    if (this.lines.length > num_entries) {
      this.setLines(this.lines.slice(0, num_entries));
    } else if (this.lines.length < num_entries) {
      while (this.lines.length < num_entries) {
        this.lines.push(this.generateRandomNumbers());
      }
      this.setLines(this.lines);
    }

    this.resetOrder();
  };

  setLines = (lines: IEntryLine[]) => {
    this.lines = lines;
    this.resetOrder();
  };

  setLine = (line_number: number, line: IEntryLine) => {
    const newLines = this.lines.slice();
    newLines[line_number] = line;
    this.lines = newLines;
    this.resetOrder();
  };

  setLineNumber = (line_number: number, position: number, value: number) => {
    if (line_number >= this.num_entries) {
      throw Error('line_number bigger than num_entries');
    }
    this.lines[line_number]['main'][position] = value;
    this.resetOrder();
  };

  removeLine = (line_number: number) => {
    this.lines.splice(line_number, 1);
    this.setNumEntries(this.num_entries - 1);
  };

  toggleLineNumber = (line_number: number, value: number) => {
    if (line_number >= this.num_entries) {
      throw Error('line_number bigger than num_entries');
    }

    const numIndex = this.lines[line_number]['main'].indexOf(value);

    if (numIndex != -1) {
      this.lines[line_number]['main'][numIndex] = null;
    } else {
      const emptySpace = this.lines[line_number]['main'].indexOf(null);

      if (emptySpace !== -1) {
        this.lines[line_number]['main'][emptySpace] = value;
      }
    }
    this.resetOrder();
  };

  setLineBonusNumber = (
    line_number: number,
    position: number,
    value: number
  ) => {
    if (line_number >= this.num_entries) {
      throw Error('line_number bigger than num_entries');
    }
    this.lines[line_number]['bonus'][position] = value;
    this.resetOrder();
  };

  toggleLineBonusNumber = (line_number: number, value: number) => {
    if (line_number >= this.num_entries) {
      throw Error('line_number bigger than num_entries');
    }

    const numIndex = this.lines[line_number]['bonus'].indexOf(value);

    if (numIndex != -1) {
      this.lines[line_number]['bonus'][numIndex] = null;
    } else {
      const emptySpace = this.lines[line_number]['bonus'].indexOf(null);

      if (emptySpace != -1) {
        this.lines[line_number]['bonus'][emptySpace] = value;
      } else {
        this.lines[line_number]['bonus'][0] = value;
      }
    }

    this.resetOrder();
  };

  setErrorPopupMessage = (message: string | null) => {
    this.errorPopupMessage = message;
  };

  generateLineRandomNumbers = (line_number: number) => {
    this.setLine(line_number, this.generateRandomNumbers());
  };

  generateRandomNumbers = () => {
    return generateRandomNumbers(
      this.root.drawStore.numberSetup.main,
      this.root.drawStore.numberSetup.bonus
    );
  };

  setDays = (days: MainDrawDay[]) => {
    this.days = days;

    this.resetOrder();
  };

  toggleDay = (day: MainDrawDay) => {
    const idx = this.days.indexOf(day);
    if (idx === -1) {
      this.days.push(day);
    } else {
      this.days.splice(idx, 1);
    }

    this.resetOrder();
  };

  setBoosted = (boosted: boolean) => {
    this.boosted = boosted;
    this.planSubcode = boosted
      ? DaymadeDrawTypePlanSubcode.BOOST
      : DaymadeDrawTypePlanSubcode.BASE;

    this.resetOrder();
  };

  setReferralId = (referralId: string | null) => {
    this.referralId = referralId;
  };

  setFirstEntryOffer = async () => {
    if (
      this.eligibleForIntroOffer &&
      !this.currentPromo &&
      !this.isReferralFlow &&
      this.isPremiumSubscription
    ) {
      const promoResults = await promoApiClient.public.getFirstEntryOffer();
      if (promoResults.status === HttpStatusCode.OK) {
        const promoCode = promoResults.body?.promoCode;
        if (promoCode?.code) {
          console.log('Setting first entry promo code:', promoCode);
          await this.setPromoCode(promoCode ? promoCode.code : null);
        }
      }
    } else {
      console.log('Not eligible for first entry offer or already set');
      // We previously cleared the promo code here, however, that causes any non-first-entry-offer promo code error
      // messages to also be cleared
    }
  };

  setPromoCode = async (promo_code: string | null) => {
    if (!promo_code) {
      localStorage.removeItem('promoCode');
      removeCookie('promo_code');
    }
    console.log('Setting promo code:', promo_code);
    this.promoCode = promo_code;

    await this.processPromoCode();
  };

  getDiscount = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentPromo = await promoApiClient.public.getPromo({
      body: {
        referralId: this.referralId,
        promoCode: this.promoCode,
        evalData: {
          isftmp: this.isCurrentUserFtmp,
          isSubscribed: false,
          drawTypeCode: this.root.drawStore.drawTypeCode,
          planCode: this.subscriptionStatus,
          planSubcode: this.planSubcode,
          numEntries: this.num_entries,
        },
      },
    });

    if (currentPromo.status === HttpStatusCode.OK) {
      // Hacky - since the FIRST_ENTRY_DISCOUNT_CODE promo code may be reapplied after another promo code fails, we
      // don't want to clear the promoErrorMessage if the current promo code is the FIRST_ENTRY_DISCOUNT_CODE. Otherwise,
      // the warning message goes away
      if (
        currentPromo.body.promoCode?.code !==
        promoCodeConstants.FIRST_ENTRY_DISCOUNT_CODE
      ) {
        this.promoErrorMessage = currentPromo.body?.message || null;
      }
      this.referralDetails = currentPromo.body?.referralDetails || null;
      this.currentPromo = currentPromo.body?.promoCode || null;

      if (this.currentPromo) {
        localStorage.setItem('promoCode', this.currentPromo?.code || '');
      } else {
        localStorage.removeItem('promoCode');
      }

      if (this.referralDetails) {
        this.setIsReferralFlow(true);
      }
    } else {
      this.promoErrorMessage = currentPromo.body?.message || null;

      localStorage.removeItem('promoCode');
      removeCookie('promo_code');

      if (this.referralId) {
        this.setIsReferralFlow(false);
        this.setReferralId(null);
        this.referralDetails = null;
        removeCookie('referral_id');
      }

      // Since the promo code failed to apply, try to reapply the first entry offer
      await this.setFirstEntryOffer();
    }
  };

  processPromoCode = async () => {
    await this.getDiscount();
    this.resetOrder();
  };

  get promoCodeApplicationResult(): Partial<IPromoCodeApplicationResult> | null {
    // We have to mimic the pre-DRIP EnterStore/EnterGlobalContext behaviour. This involves 'promoCodeApplicationResult'
    // returning an object with 'successful:true' when we want to display the promo codes discount details. The discount
    // details are exposed by promoCodeMessages in EnterGlobalContext
    if (this.isCurrentPromoFirstEntryDiscount) {
      // We don't want to show any promo code details when it is the first entry discount, so return null
      return null;
    }
    const message = this.promoCodeMessage;
    if (message) {
      return {
        successful: true,
      };
    } else {
      return null;
    }
  }

  get promoCodeMessage() {
    if (!this.currentPromo || !this.isPaidSubscription) {
      return null;
    }

    const { rewardType, rewardValue, rewardCount } = this.currentPromo;

    switch (rewardType) {
      case PromoRewardType.FREE_ENTRY: {
        const numFreeEntries = rewardValue;

        return `Promo code successfully applied. ${numFreeEntries} ${
          numFreeEntries === 1 ? 'free entry ' : 'free entries '
        } will be added.`;
      }

      case PromoRewardType.DISCOUNT_PERCENTAGE: {
        return `Your promo code has successfully been applied. Your order has been discounted by ${rewardValue}%${
          rewardCount && rewardCount > 0
            ? ` and will continue to be discounted for ${rewardCount} more week${
                rewardCount > 1 ? 's' : ''
              }`
            : ''
        }.`;
      }
      case PromoRewardType.DISCOUNT_ENTRY: {
        return `Your order has been discounted by the cost of ${
          rewardValue === 1 ? 'an entry' : `${rewardValue} entries`
        } ${
          rewardCount && rewardCount > 0
            ? ` and will continue to be discounted for ${rewardCount} more week${
                rewardCount > 1 ? 's' : ''
              }`
            : ''
        }.`;
      }
      case PromoRewardType.FIXED_DISCOUNT: {
        return `Your order has been discounted by ${convertMicroCost(rewardValue || 0)}${
          rewardCount && rewardCount > 0
            ? ` and will continue to be discounted for ${rewardCount} more week${
                rewardCount > 1 ? 's' : ''
              }`
            : ''
        }.`;
      }
      default: {
        console.error(`Don't know what to do with rewardCode: ${rewardType}`);
      }
    }
  }

  handleContinueToPayment = async () => {
    if (this.haveValidAdyenPaymentSession && this.orderId) {
      return {
        orderId: this.orderId,
        adyenPaymentSession: this.adyenPaymentSession,
      };
    }

    const affiliateFutureAffc = getCookie('Affc', null);

    const subscriptionDetails =
      this.entryMethod === EntryMethod.OneOff
        ? null
        : createSubscriptionDetailsObject(
            this.entryMethod as any,
            this.paymentSchedule === PaymentSchedule.MONTHLY
          );

    const sourceFor3DS = this.upgradePageSource
      ? SOURCE_QUERY_PARAM.UPGRADE
      : 'enter-now';

    const utmParams = fetchUtmParamsJoinedWithFbDataFromLocalStorage();
    const attributionParams: Record<string, string> = {};

    if (affiliateFutureAffc) {
      attributionParams['affiliateFutureAffc'] = affiliateFutureAffc;
    }

    const result = await subscriptionApiClient.auth.createSubscription({
      body: {
        drawTypeCode: 'DAYMADEDRAW',
        drawSchedulePlans: getDrawScheduleIds(
          this.drawSchedules,
          this.days
        ).map((id) => ({
          drawScheduleId: id,
          drawTypePlanCode: this.planCode,
          drawTypePlanSubcode: this.planSubcode,
        })),
        entries: this.lines as TEntryLine[],
        isUpgrade: this.isUpgradeFlow,
        promoCode: this.currentPromo?.code || undefined,
        // ENG-1060 - Referrals temporarily disabled
        //referralId: this.referralId || undefined,
        expectedCost: this.orderCost.cost,
        utmParams: utmParams as any,
        attributionParams,
        sourceFor3DS,
        evalData: {
          planCode: this.subscriptionStatus,
        },
      },
    });

    if (result.status === HttpStatusCode.OK) {
      trackInitiateCheckoutEvent();

      await trackEecCheckoutStep4(
        EecProduct.LOTTERY,
        this.entryMethod,
        `${this.lines.length} entry`,
        this.orderCost.price,
        false
      );

      this.setOrder(
        result.body.orderId,
        result.body.adyenPaymentSession ?? null
      );

      return {
        orderId: result.body.orderId,
        adyenPaymentSession: result.body.adyenPaymentSession ?? null,
      };
    } else if (result.status === HttpStatusCode.BAD_REQUEST) {
      console.error('createSubscription error ', result);

      mixpanel.track('Enter Checkout - Create Subscription Error', {
        error: result.body || 'Unknown error',
      });

      let errorMessage = result.body.message || 'Error creating subscription';

      if (result.body.reason) {
        switch (result.body.reason) {
          case 'WOULD_EXCEED_SPEND_LIMIT':
            errorMessage = getSpendLimitErrorMessage(
              `${this.isUpgradeFlow ? 'upgrade' : 'create'} subscription`
            );
            break;
          case 'HAS_ACTIVE_SUBSCRIPTION':
            errorMessage = result.body.message;
            break;
          default:
            errorMessage = 'Sorry, an error has occurred';
            exhaustiveCheck(result.body.reason);
        }
        this.setOrderError(errorMessage);
      } else {
        this.setOrderError('Sorry, an error has occurred');
      }

      this.root.UIStore.displayToast({
        title: errorMessage,
        timeout: 3500,
      });

      return null;
    }
  };

  checkOrderStatusAndAdvanceToConfirmation = async (
    orderIdRef: React.MutableRefObject<string | null>,
    orderCompletionTimerRef: React.MutableRefObject<any>
  ) => {
    if (!orderIdRef.current) {
      return;
    }

    const orderDetails = await orderApiClient.auth.getOrderDetails({
      params: {
        order_id: orderIdRef.current,
      },
    });

    if (orderDetails.status === HttpStatusCode.OK) {
      if (orderDetails.body.order.status === OrderStatusEnum.COMPLETED) {
        await this.onSuccessfulPayment(orderDetails.body);
      } else {
        orderCompletionTimerRef.current = setTimeout(
          () =>
            this.checkOrderStatusAndAdvanceToConfirmation(
              orderIdRef,
              orderCompletionTimerRef
            ),
          2000
        );
      }
    } else if (orderDetails.status === HttpStatusCode.NOT_FOUND) {
      this.setOrderError('Order not found');
      console.error('Order not found', orderIdRef.current);
      orderCompletionTimerRef.current = null;
      this.root.UIStore.displayToast({
        title: 'Order not found',
        timeout: 3500,
      });
    } else {
      // navigate to account page
      Router.push('/account');
      this.root.UIStore.displayToast({
        title: 'Error fetching order details',
        timeout: 3500,
      });
    }
  };

  onSuccessfulPayment = async (orderDetails: TOrderDetails) => {
    removeCookie('referral_id');
    removeCookie('promo_code');
    localStorage.removeItem('promoCode');
    localStorage.removeItem('promo_code');

    this.setReferralId(null);
    this.setPromoCode(null);
    this.setOrderCompleted(true);
    this.setIsUpgradeFlow(false);
    this.adyenPaymentSession = null;
    this.adyenPaymentSessionTime = null;

    await trackPurchase(
      orderDetails.order.totalFiatAmount.toString(),
      orderDetails.order.id,
      orderDetails.order,
      orderDetails.drawOrder,
      orderDetails.drawSchedulePlans,
      this.root.userStore.currentUser?.emailAddress,
      orderDetails.promoCode,
      this.root.userStore.currentUser?.id,
      {
        drawDays: orderDetails.drawSchedulePlans
          ?.map((plan) => plan.drawSchedule.code)
          .join(','),
        numEntries: orderDetails.drawOrder?.entries.length,
        entryMethod: orderDetails.drawSchedulePlans?.[0].drawTypePlan.code,
        boosted: orderDetails.drawSchedulePlans?.some(
          (plan) =>
            plan.drawTypePlan.subcode === DaymadeDrawTypePlanSubcode.BOOST
        ),
        billings: orderDetails.order.totalFiatAmount,
        cost: orderDetails.drawOrder?.price,
      }
    );

    await navigateToConfirmation(
      Router,
      orderDetails.order.id,
      true,
      this.entryMethod
    );
    await this.root.userStore.updateCurrentUser();
  };

  setOrder = (
    orderId: string,
    adyenPaymentSession: TAdyenPaymentSession | null
  ) => {
    this.orderId = orderId;
    this.adyenPaymentSession = adyenPaymentSession;
    this.adyenPaymentSessionTime = Date.now();
    this.orderCompleted = false;
  };

  /** Marks the order as completed. This is used to determine whether we have very recently completed an order */
  setOrderCompleted = (completed: boolean) => {
    this.orderCompleted = completed;
  };

  // Call this instead of handleContinueToPayment directly from the component
  maybeContinueToPayment = async () => {
    // Only continue if not completed, not already have a valid session, and all required state
    if (
      this.orderCompleted ||
      this.haveValidAdyenPaymentSession ||
      !this.isValid ||
      !this.currentUser
    ) {
      return;
    }
    await this.handleContinueToPayment();
  };

  get haveValidAdyenPaymentSession() {
    const tenMinutes = 10 * 60 * 1000;
    return (
      this.adyenPaymentSession &&
      this.adyenPaymentSessionTime &&
      Date.now() - this.adyenPaymentSessionTime < tenMinutes
    );
  }

  setOrderError = (error: string | null) => {
    this.orderError = error;
  };

  validateOrder = () => {
    const numberSetup = this.root.drawStore.numberSetup;

    const hasErrors =
      this.lines.some(
        (line) =>
          !lineIsValid(line.main, numberSetup.main) ||
          !lineIsValid(line.bonus, numberSetup.bonus)
      ) || ticketContainsDuplicateLines(this.lines);

    return !hasErrors;
  };

  get isCurrentPromoFirstEntryDiscount() {
    return (
      this.currentPromo?.code === promoCodeConstants.FIRST_ENTRY_DISCOUNT_CODE
    );
  }

  get isUserStoreInitialized() {
    return this.root.userStore.initialized;
  }

  get isValid() {
    return this.root.drawStore.numberSetup && this.validateOrder();
  }

  get confirmed() {
    return this.validateOrder();
  }

  chooseNumbers = async (onChoose?: () => void) => {
    await trackEecAddToCart(
      EecProduct.LOTTERY,
      this.entryMethod,
      `${this.lines.length} entry`,
      this.orderCost.price
    );
    await trackEecCheckoutStep1(
      EecProduct.LOTTERY,
      this.entryMethod,
      `${this.lines.length} entry`,
      this.orderCost.price
    );
    await trackEecCheckoutStep2();

    if (onChoose) {
      onChoose();
    } else {
      if (!this.currentUser) {
        //authenticate
        Router.push(`/register?redirect=${encodeURIComponent('checkout')}`);
      } else {
        //go to payment page
        Router.push(`/checkout`);
      }
    }
  };

  setIsReferralFlow = (isReferralFlow: boolean) => {
    this.isReferralFlow = isReferralFlow;
  };

  setLoadingReferralBonusScheme = (loading: boolean) => {
    this.loadingReferralBonusScheme = loading;
  };

  setHiddenFeatures = (features: string[]) => {
    this.hiddenFeatures = features;
  };

  hideFeatures = () => {
    this.setHiddenFeatures([
      EnterFlowFeature.MULTIPLE_ENTRIES,
      EnterFlowFeature.MULTIPLE_DRAWS,
      EnterFlowFeature.BOOST_OR_TOP_UP,
    ]);
  };

  fetchReferralDetails = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    // ENG-1060 - Referrals temporarily disabled
    // this.setLoadingReferralBonusScheme(true);
    const referralId = Router.query?.ri || getCookie('referral_id');

    if (referralId) {
      // ENG-1060 - Referrals temporarily disabled
      this.root.UIStore.displayToast({
        title: 'Sorry, our referral scheme will be back in the new year!',
        timeout: 3500,
        color: 'red',
      });
      removeCookie('referral_id');
    }

    /* ENG-1060 - Referrals temporarily disabled
    if (!referralId) {
      this.setLoadingReferralBonusScheme(false);
      return;
    }

    await this.setReferralId(referralId);

    if (!this.referralDetails || !this.currentPromo) {
      this.setLoadingReferralBonusScheme(false);
      return;
    }

    const isReferralFlow = this.currentPromo.promoType === PromoType.REFERRAL;

    this.setIsReferralFlow(isReferralFlow);
    this.setLoadingReferralBonusScheme(false);
    if (isReferralFlow) {
      this.hideFeatures();
    }
    */
  };

  navigateToEnterTest = (enterLink: string) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.isReferralFlow) {
      return Router.push('/enter-now');
    }

    return Router.push('/enternow/1');
  };

  navigateToCheckout = (isReferralFlow?: boolean) => {
    if (typeof window === 'undefined') {
      return;
    }

    return Router.push(
      `/register?redirect=${isReferralFlow ? 'checkout' : 'enter/payment'}`
    );
  };
}

// const discountFirstEntry =
//   entryMethod === EntryMethod.PremiumSubscription &&
//   !isReferralFlow &&
//   eligibleForIntroOffer;

// const firstEntryDiscount = discountFirstEntry ? 0.5 : 0;

// const promoCodeMessages = [];

// if (promoCodeApplicationResult) {
//   const promoCodeMessage = getPromoCodeMessage();

//   if (promoCodeMessage) {
//     promoCodeMessages.push(promoCodeMessage);
//   }
// }

// if (promoCodeMessages.length > 0 && firstEntryDiscount) {
//   promoCodeMessages.push('Your 1st entry has been discounted by 50%');
// }
